/**
 * useEnterpriseProctoring.js
 * Enterprise-grade AI proctoring hook for all Cognique assessment types.
 * Provides unified violation tracking, AI face checks, and submit-ready proctoring report.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as faceapi from '@vladmandic/face-api';

const LOCAL_MODEL_URI = '/models';
const CDN_MODEL_URI = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
const DETECTION_INTERVAL_MS = 800;
const VOICE_SAMPLE_INTERVAL_MS = 2000;
const GAZE_THRESHOLD = 0.18;

let modelsLoaded = false;
let modelLoadPromise = null;

const ALERT_MESSAGES = {
  tab_switch: 'Warning: Do not switch tabs or applications during the assessment.',
  face_absent: 'Please ensure your face is clearly visible in the camera.',
  multiple_faces: 'Multiple faces detected. Only the candidate should be in frame.',
  gaze_away: 'Please keep your eyes on the screen.',
  head_down: 'Please look at the screen. Do not look down.',
  copy_paste: 'Copy/paste is not allowed during this assessment.',
  fullscreen_exit: 'Please remain in fullscreen mode during the assessment.',
  voice_detected: 'Please maintain silence during the assessment.'
};

const average = (values) => values.reduce((sum, value) => sum + value, 0) / Math.max(values.length, 1);

const distance = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

function computeIntegrityScore(metrics) {
  let score = 100;
  score -= metrics.tab_switches * 5;
  score -= metrics.face_absent_count * 3;
  score -= metrics.multiple_faces_count * 10;
  score -= metrics.gaze_away_count * 2;
  score -= metrics.head_down_count * 4;
  score -= metrics.copy_paste_attempts * 8;
  score -= metrics.fullscreen_exits * 5;
  score -= metrics.voice_detected_count * 3;
  return Math.max(0, Math.round(score));
}

async function loadModels() {
  if (modelsLoaded) return;
  if (!modelLoadPromise) {
    modelLoadPromise = (async () => {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri(LOCAL_MODEL_URI);
        await faceapi.nets.faceLandmark68Net.loadFromUri(LOCAL_MODEL_URI);
      } catch (_localError) {
        // ADDED: CDN fallback when local model files are not present.
        await faceapi.nets.tinyFaceDetector.loadFromUri(CDN_MODEL_URI);
        await faceapi.nets.faceLandmark68Net.loadFromUri(CDN_MODEL_URI);
      }
      modelsLoaded = true;
    })();
  }
  return modelLoadPromise;
}

export default function useEnterpriseProctoring({
  assessmentKey,
  videoRef,
  enabled = true,
  onViolation = () => {},
  onForcedSubmit = () => {},
  maxViolations = 10
}) {
  const startTimeRef = useRef(Date.now());
  const forcedSubmitTriggeredRef = useRef(false);
  const violationCooldownRef = useRef({});

  const faceAbsentCountRef = useRef(0);
  const faceAbsentDurationRef = useRef(0);
  const multipleFacesCountRef = useRef(0);
  const gazeAwayCountRef = useRef(0);
  const gazeAwayDurationRef = useRef(0);
  const headDownCountRef = useRef(0);
  const tabSwitchCountRef = useRef(0);
  const copyPasteCountRef = useRef(0);
  const fullscreenExitCountRef = useRef(0);
  const voiceDetectedCountRef = useRef(0);
  const rapidAnswerCountRef = useRef(0);
  const idleTimeCountRef = useRef(0);
  const flaggedEventsRef = useRef([]);

  const faceAbsentStartRef = useRef(null);
  const gazeAwayStartRef = useRef(null);
  const headDownStartRef = useRef(null);
  const alertTimerRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  const fullscreenRequestedRef = useRef(false);
  const wasFullscreenRef = useRef(false);
  const micIntervalRef = useRef(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const lastAnswerTimeRef = useRef(Date.now());

  const [currentAlert, setCurrentAlert] = useState(null);
  const [faceStatus, setFaceStatus] = useState('unknown');
  const [gazeStatus, setGazeStatus] = useState('on_screen');
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [violations, setViolations] = useState(0);
  const [integrityScore, setIntegrityScore] = useState(100);
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [reportVersion, setReportVersion] = useState(0);

  const touchReport = useCallback(() => {
    setReportVersion((prev) => prev + 1);
  }, []);

  const dismissAlert = useCallback(() => {
    setCurrentAlert(null);
  }, []);

  const calculateAndSetIntegrity = useCallback(() => {
    const score = computeIntegrityScore({
      tab_switches: tabSwitchCountRef.current,
      face_absent_count: faceAbsentCountRef.current,
      multiple_faces_count: multipleFacesCountRef.current,
      gaze_away_count: gazeAwayCountRef.current,
      head_down_count: headDownCountRef.current,
      copy_paste_attempts: copyPasteCountRef.current,
      fullscreen_exits: fullscreenExitCountRef.current,
      voice_detected_count: voiceDetectedCountRef.current
    });
    setIntegrityScore(score);
  }, []);

  const showAlert = useCallback((message) => {
    setCurrentAlert(message);
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
    }
    alertTimerRef.current = setTimeout(() => {
      setCurrentAlert(null);
      alertTimerRef.current = null;
    }, 5000);
  }, []);

  const triggerViolation = useCallback(
    (type, detail) => {
      const now = Date.now();
      const lastTriggeredAt = violationCooldownRef.current[type] || 0;
      if (now - lastTriggeredAt < 2500) return;
      violationCooldownRef.current[type] = now;

      flaggedEventsRef.current.push({ type, timestamp: now, detail });
      touchReport();

      setViolations((prev) => {
        const next = prev + 1;
        if (next >= maxViolations && !forcedSubmitTriggeredRef.current) {
          forcedSubmitTriggeredRef.current = true;
          onForcedSubmit();
          showAlert('Assessment terminated due to excessive violations.');
        }
        return next;
      });

      calculateAndSetIntegrity();
      onViolation(type, detail);
      showAlert(ALERT_MESSAGES[type] || detail || 'Proctoring warning.');
    },
    [calculateAndSetIntegrity, maxViolations, onForcedSubmit, onViolation, showAlert, touchReport]
  );

  const analyzeGaze = useCallback(
    (landmarks) => {
      const points = landmarks.positions || [];
      if (!points.length) return;

      const leftEyeIndices = [36, 37, 38, 39, 40, 41];
      const rightEyeIndices = [42, 43, 44, 45, 46, 47];
      const leftEyeY = average(leftEyeIndices.map((i) => points[i]?.y || 0));
      const rightEyeY = average(rightEyeIndices.map((i) => points[i]?.y || 0));
      const eyeMidY = (leftEyeY + rightEyeY) / 2;

      const noseY = points[30]?.y || 0;
      const chinY = points[8]?.y || eyeMidY + 1;
      const headTiltRatio = (noseY - eyeMidY) / Math.max(1, chinY - eyeMidY);

      const leftEyeCenter = {
        x: ((points[36]?.x || 0) + (points[39]?.x || 0)) / 2,
        y: ((points[36]?.y || 0) + (points[39]?.y || 0)) / 2
      };
      const rightEyeCenter = {
        x: ((points[42]?.x || 0) + (points[45]?.x || 0)) / 2,
        y: ((points[42]?.y || 0) + (points[45]?.y || 0)) / 2
      };

      const eyeCenter = {
        x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
        y: (leftEyeCenter.y + rightEyeCenter.y) / 2
      };

      const videoWidth = videoRef.current?.videoWidth || 1;
      const videoHeight = videoRef.current?.videoHeight || 1;
      const frameCenter = { x: videoWidth / 2, y: videoHeight / 2 };

      const gazeDeviation = distance(eyeCenter, frameCenter) / Math.max(videoWidth, videoHeight);
      const now = Date.now();

      if (headTiltRatio > 0.72) {
        setGazeStatus('down');
        headDownCountRef.current += 1;
        touchReport();
        if (!headDownStartRef.current) headDownStartRef.current = now;
        if (now - headDownStartRef.current >= 3000) {
          triggerViolation('head_down', 'Looking down for more than 3 seconds');
          headDownStartRef.current = now;
        }
      } else {
        headDownStartRef.current = null;
      }

      if (gazeDeviation > GAZE_THRESHOLD) {
        setGazeStatus('away');
        gazeAwayCountRef.current += 1;
        touchReport();
        if (!gazeAwayStartRef.current) gazeAwayStartRef.current = now;
        gazeAwayDurationRef.current += 1;
        if (now - gazeAwayStartRef.current >= 4000) {
          triggerViolation('gaze_away', 'Gaze away from screen for more than 4 seconds');
          gazeAwayStartRef.current = now;
        }
      } else if (headTiltRatio <= 0.72) {
        setGazeStatus('on_screen');
        gazeAwayStartRef.current = null;
      }
    },
    [touchReport, triggerViolation, videoRef]
  );

  const requestFullscreen = useCallback(() => {
    const target = document.documentElement;
    if (!target?.requestFullscreen) return;
    target.requestFullscreen().catch(() => {
      showAlert('Could not enter fullscreen. Please press F11.');
    });
  }, [showAlert]);

  const recordAnswer = useCallback(
    (timeSpentMs) => {
      if (Number.isFinite(timeSpentMs) && timeSpentMs < 2000) {
        rapidAnswerCountRef.current += 1;
      }
      lastAnswerTimeRef.current = Date.now();
      touchReport();
    },
    [touchReport]
  );

  const recordIdle = useCallback(() => {
    idleTimeCountRef.current += 1;
    touchReport();
  }, [touchReport]);

  useEffect(() => {
    if (!enabled) return undefined;
    let isUnmounted = false;

    const setupDetection = async () => {
      await loadModels();
      if (isUnmounted) return;

      detectionIntervalRef.current = setInterval(async () => {
        const video = videoRef.current;
        if (!video || video.readyState < 2) {
          setIsWebcamActive(false);
          return;
        }
        setIsWebcamActive(true);

        try {
          const detections = await faceapi
            .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks();

          const faceCount = detections.length;
          const now = Date.now();

          if (faceCount === 0) {
            setFaceStatus('absent');
            faceAbsentCountRef.current += 1;
            touchReport();

            if (!faceAbsentStartRef.current) {
              faceAbsentStartRef.current = now;
            }
            faceAbsentDurationRef.current += 1;

            if (now - faceAbsentStartRef.current >= 5000) {
              triggerViolation('face_absent', 'Face not visible in camera');
              faceAbsentStartRef.current = now;
            }
            return;
          }

          faceAbsentStartRef.current = null;

          if (faceCount > 1) {
            setFaceStatus('multiple');
            multipleFacesCountRef.current += 1;
            touchReport();
            triggerViolation('multiple_faces', `${faceCount} faces detected`);
            return;
          }

          setFaceStatus('detected');
          analyzeGaze(detections[0].landmarks);
        } catch (_err) {
          // Ignore per-frame detection errors.
        }
      }, DETECTION_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabSwitchCountRef.current += 1;
        touchReport();
        triggerViolation('tab_switch', 'Switched to another tab or application');
      }
    };

    const handleWindowBlur = () => {
      tabSwitchCountRef.current += 1;
      touchReport();
      triggerViolation('tab_switch', 'Application lost focus');
    };

    const handleFullscreenChange = () => {
      const fullscreenNow = Boolean(document.fullscreenElement);
      setIsFullscreen(fullscreenNow);
      if (wasFullscreenRef.current && !fullscreenNow) {
        fullscreenExitCountRef.current += 1;
        touchReport();
        triggerViolation('fullscreen_exit', 'Exited fullscreen mode');
        showAlert('Please return to fullscreen mode to continue your assessment.');
      }
      wasFullscreenRef.current = fullscreenNow;
    };

    const blockCopyPaste = (event, keyLabel = 'shortcut') => {
      event.preventDefault();
      copyPasteCountRef.current += 1;
      touchReport();
      triggerViolation('copy_paste', `Blocked keyboard shortcut: ${keyLabel}`);
    };

    const handleCopy = (event) => blockCopyPaste(event, 'copy');
    const handlePaste = (event) => blockCopyPaste(event, 'paste');
    const handleCut = (event) => blockCopyPaste(event, 'cut');
    const handleContextMenu = (event) => event.preventDefault();
    const handleKeydown = (event) => {
      const key = String(event.key || '').toLowerCase();
      const blocked =
        (event.ctrlKey && ['c', 'v', 'x', 'a', 'u'].includes(key)) ||
        key === 'f12' ||
        key === 'printscreen';
      if (blocked) {
        blockCopyPaste(event, event.key);
      }
    };

    const setupVoiceDetection = async () => {
      try {
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        const source = audioContextRef.current.createMediaStreamSource(micStreamRef.current);
        source.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;

        micIntervalRef.current = setInterval(() => {
          if (!analyserRef.current) return;
          const data = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(data);
          const avg = data.reduce((sum, value) => sum + value, 0) / Math.max(data.length, 1);
          if (avg > 20) {
            voiceDetectedCountRef.current += 1;
            touchReport();
            triggerViolation('voice_detected', 'Audio/voice detected near device');
          }
        }, VOICE_SAMPLE_INTERVAL_MS);
      } catch (_err) {
        // Non-blocking by design when microphone permission is denied.
      }
    };

    setupDetection();
    setupVoiceDetection();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeydown);

    const fullscreenTimer = setTimeout(() => {
      if (!fullscreenRequestedRef.current) {
        fullscreenRequestedRef.current = true;
        requestFullscreen();
      }
    }, 1000);

    return () => {
      isUnmounted = true;
      clearTimeout(fullscreenTimer);

      if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
      if (micIntervalRef.current) clearInterval(micIntervalRef.current);
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);

      detectionIntervalRef.current = null;
      micIntervalRef.current = null;
      alertTimerRef.current = null;

      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeydown);

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [analyzeGaze, enabled, requestFullscreen, showAlert, touchReport, triggerViolation, videoRef]);

  useEffect(() => {
    calculateAndSetIntegrity();
  }, [calculateAndSetIntegrity, reportVersion, violations]);

  const proctoringReport = useMemo(
    () => ({
      tab_switches: tabSwitchCountRef.current,
      rapid_answers: rapidAnswerCountRef.current,
      violations,
      integrity_score: integrityScore,
      face_absent_count: faceAbsentCountRef.current,
      face_absent_duration: faceAbsentDurationRef.current,
      multiple_faces_count: multipleFacesCountRef.current,
      gaze_away_count: gazeAwayCountRef.current,
      gaze_away_duration: gazeAwayDurationRef.current,
      head_down_count: headDownCountRef.current,
      voice_detected_count: voiceDetectedCountRef.current,
      copy_paste_attempts: copyPasteCountRef.current,
      fullscreen_exits: fullscreenExitCountRef.current,
      assessment_key: assessmentKey,
      attempt_duration: Math.floor((Date.now() - startTimeRef.current) / 1000),
      capture_source: 'webcam',
      proctoring_active: enabled,
      flagged_events: [...flaggedEventsRef.current]
    }),
    [assessmentKey, enabled, integrityScore, reportVersion, violations]
  );

  return {
    proctoringReport,
    isWebcamActive,
    currentAlert,
    dismissAlert,
    violations,
    integrityScore,
    faceStatus,
    gazeStatus,
    isFullscreen,
    requestFullscreen,
    recordAnswer,
    recordIdle
  };
}
